import type { LogoutOptions } from '@auth0/auth0-spa-js';
import { clearInitialBillingReceipts } from '#~/api/agent/initial-billing.js';
import { clearOnboardingResume } from '#~/onboarding/resume.js';
import { clearRecordingSavedSession } from '#~/notifications/recording-saved-session.js';

/**
 * Clear the onboarding reload note and initial-billing receipts, then log out.
 *
 * The website logout passes Auth0 options (`returnTo`). The desktop logout
 * takes none. Both must drop the note first: Electron logout does not wipe
 * window.localStorage, so the next account on this profile would resume it.
 * Billing receipts live in sessionStorage, which also survives sign-out in the
 * same tab, so the next account would read them as its own unfinished setup.
 *
 * Per rule: think-in-systems (ai.knowledge/global).
 */
export const logoutAndClearResume = async (input: {
  isElectron: boolean;
  hasUser: boolean;
  options?: LogoutOptions | undefined;
  webLogout: (options?: LogoutOptions) => void | Promise<void>;
  electronLogout: () => void;
  onElectronLoggedOut?: () => void;
}): Promise<void> => {
  clearOnboardingResume();
  clearInitialBillingReceipts();
  clearRecordingSavedSession();
  if (!input.isElectron) {
    // Forward completion and rejection so callers can await the browser handoff.
    return input.webLogout(input.options);
  }
  if (!input.hasUser) input.onElectronLoggedOut?.();
  input.electronLogout();
};
